/**
 * <testimonials-carousel>
 * Ventana fija que muestra EXACTAMENTE N tarjetas completas (3 en desktop,
 * 1 en móvil). El riel se desliza de a una: sale una por un lado y entra otra
 * por el otro, en loop infinito, sin cortar tarjetas.
 * - Arrastre con mouse (PC) y swipe (touch).
 * - Puntos: uno por testimonio, marcan/llevan al del centro.
 * Se clona un set completo a cada lado para el loop y se "reengancha" el riel
 * sin salto al terminar cada animación.
 */
class TestimonialsCarousel extends HTMLElement {
  #gap = 24;
  #perView = 3;
  #pos = 0;
  #dragging = false;

  connectedCallback() {
    this.window = this.querySelector('.testimonials__window');
    this.track = this.querySelector('.testimonials__track');
    this.dotsWrap = this.querySelector('.testimonials__dots');
    if (!this.window || !this.track) return;

    this.originals = Array.from(this.track.children);
    this.count = this.originals.length;
    if (this.count === 0) return;

    // En el editor de temas: mostrar todas las tarjetas (sin carrusel).
    if (window.Shopify && window.Shopify.designMode) {
      this.classList.add('is-editor');
      this.cards = this.originals;
      return;
    }

    this.#readVars();
    this.enabled = this.count > this.#perView;

    if (this.enabled) {
      this.#buildClones();
      this.cards = Array.from(this.track.children);
      this.#pos = this.count; // arrancamos en el primer original
    } else {
      this.cards = this.originals;
      this.#pos = 0;
    }

    this.#buildDots();
    this.#enableDrag();
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.track.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform') this.#settleWrap();
    });

    requestAnimationFrame(() => {
      this.#readVars();
      this.#apply(false);
    });
  }

  #readVars() {
    const cs = getComputedStyle(this.track);
    const pv = parseInt(cs.getPropertyValue('--per-view'), 10);
    this.#perView = Number.isFinite(pv) && pv > 0 ? pv : 3;
    const gap = parseFloat(cs.columnGap || cs.gap);
    this.#gap = Number.isFinite(gap) ? gap : 24;
  }

  #step() {
    const card = this.cards && this.cards[0];
    return card ? card.getBoundingClientRect().width + this.#gap : 0;
  }

  #centerOffset() {
    return Math.floor(this.#perView / 2);
  }

  #buildClones() {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    this.originals.forEach((card) => {
      before.appendChild(this.#clone(card));
      after.appendChild(this.#clone(card));
    });
    this.track.insertBefore(before, this.originals[0]);
    this.track.appendChild(after);
  }

  #clone(card) {
    const clone = card.cloneNode(true);
    clone.removeAttribute('id');
    Array.from(clone.attributes).forEach((attr) => {
      if (attr.name.includes('shopify')) clone.removeAttribute(attr.name);
    });
    clone.dataset.clone = 'true';
    clone.setAttribute('aria-hidden', 'true');
    return clone;
  }

  #buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    if (!this.enabled) {
      this.dots = [];
      return;
    }
    for (let i = 0; i < this.count; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'testimonials__dot';
      dot.setAttribute('aria-label', `Ir al testimonio ${i + 1}`);
      dot.addEventListener('click', () => this.#goToLogical(i));
      this.dotsWrap.appendChild(dot);
    }
    this.dots = Array.from(this.dotsWrap.children);
  }

  #apply(animate = true) {
    const step = this.#step();
    this.track.classList.toggle('is-animating', animate);
    this.track.style.transform = `translateX(${-this.#pos * step}px)`;
    this.#updateActive();
    this.#updateDots();
  }

  /** Reengancha el riel dentro del set del medio (loop invisible). */
  #settleWrap() {
    if (!this.enabled) return;
    let changed = false;
    if (this.#pos >= 2 * this.count) {
      this.#pos -= this.count;
      changed = true;
    } else if (this.#pos < this.count) {
      this.#pos += this.count;
      changed = true;
    }
    if (changed) {
      const step = this.#step();
      this.track.classList.remove('is-animating');
      this.track.style.transform = `translateX(${-this.#pos * step}px)`;
      void this.track.offsetWidth; // forzar reflow
    }
    this.#updateActive();
    this.#updateDots();
  }

  next() {
    if (!this.enabled) return;
    this.#pos += 1;
    this.#apply(true);
  }

  prev() {
    if (!this.enabled) return;
    this.#pos -= 1;
    this.#apply(true);
  }

  #goToLogical(logical) {
    if (!this.enabled) return;
    const offset = ((logical - this.#centerOffset()) % this.count + this.count) % this.count;
    this.#pos = this.count + offset;
    this.#apply(true);
  }

  #updateActive() {
    if (!this.cards) return;
    const centerAbs = this.#pos + this.#centerOffset();
    this.cards.forEach((card, i) => {
      card.classList.toggle('is-active', this.#perView > 1 && i === centerAbs);
    });
  }

  #updateDots() {
    if (!this.dots || this.dots.length === 0) return;
    const centerAbs = this.#pos + this.#centerOffset();
    const logical = ((centerAbs % this.count) + this.count) % this.count;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === logical ? 'true' : 'false'));
  }

  #onResize = () => {
    this.#readVars();
    this.#apply(false);
  };

  #enableDrag() {
    const el = this.window;
    let startX = 0;
    let base = 0;
    let moved = false;

    const onEnd = (event) => {
      if (!this.#dragging) return;
      this.#dragging = false;
      this.window.classList.remove('is-grabbing');
      const clientX = event && event.clientX != null ? event.clientX : startX;
      const dx = clientX - startX;
      const threshold = this.#step() / 4;
      if (dx <= -threshold) this.next();
      else if (dx >= threshold) this.prev();
      else this.#apply(true);
    };

    el.addEventListener('pointerdown', (event) => {
      if (!this.enabled) return;
      this.#dragging = true;
      moved = false;
      startX = event.clientX;
      base = -this.#pos * this.#step();
      this.track.classList.remove('is-animating');
      this.window.classList.add('is-grabbing');
    });

    el.addEventListener('pointermove', (event) => {
      if (!this.#dragging) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      this.track.style.transform = `translateX(${base + dx}px)`;
    });

    el.addEventListener('pointerup', onEnd);
    el.addEventListener('pointercancel', onEnd);
    el.addEventListener('pointerleave', onEnd);
    el.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }
}

if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}
